import React, { useEffect, useMemo, useState } from 'react';
import { Card, Modal, Spinner, Table } from 'react-bootstrap';
import { useFirestore } from '../contexts/FirestoreContext';

// --- helpers ---
const toDate = (ts) => (ts && typeof ts.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : null));
const ageInDays = (d) => (d ? Math.floor((new Date() - d) / (1000 * 60 * 60 * 24)) : null);
const bucketKey = (age) => {
  if (age == null) return null;
  if (age <= 30) return '0_30';
  if (age <= 90) return '31_90';
  if (age <= 180) return '91_180';
  return 'gt_180';
};
const BUCKETS = [
  { key: '0_30', label: '30', display: '0-30 Days' },
  { key: '31_90', label: '31-90', display: '31-90 Days' },
  { key: '91_180', label: '91-180', display: '91-180 Days' },
  { key: 'gt_180', label: '>180', display: '>180 Days' },
];
const blankCounts = () => ({ '0_30': 0, '31_90': 0, '91_180': 0, 'gt_180': 0, total: 0 });
const dash = (n) => (n > 0 ? n : '–');
const todayStr = () => {
  const d = new Date();
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const resolveModelName = (v, modelCodes = {}) => {
  const factory = (v?.strFactory ?? v?.strMake ?? '').trim();
  const factory2 = factory.toLowerCase().includes('ford') ? 'Ford' : factory.toLowerCase().includes('gm') ? 'GM' : factory;
  const model = (v?.strModel ?? '').trim();
  if (!factory && !model) return '';
  const k1 = `${factory2} - ${model}`;
  const k2 = `${factory2} - ${model.substring(0, 3)}`;
  console.log("vehicle: ", v?.strVin?.slice(-8))
  console.log('Resolving model name for:', { factory, model, k1, k2 });
  const modelName2 = modelCodes[k1] || modelCodes[k2] || model || ''
  console.log('Resolved Model Name:', modelName2 );
  return modelCodes[k1] || modelCodes[k2] || model || '';
};

export default function ActiveCustomerSummaryReport() {
  const { onSnapshotData } = useFirestore();

  const [vehicles, setVehicles] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [modelCodes,setModelCodes] = useState([])
  const [showModal, setShowModal] = useState(false);

  // live: vehicles, sales orders, customers
  useEffect(() => {
    const uV = onSnapshotData('vehicles', (v) => { setVehicles(v || []); });
    const uS = onSnapshotData('salesOrders', (s) => { setSalesOrders(s || []); });
    const uC = onSnapshotData('customers', (c) => { setCustomers(c || []); });
    const uM = onSnapshotData('modelCodes', (m) => {
      const modelCodesMap = m.reduce((map, obj) => {
        map[obj.strFactory + " - " + obj.strModel] = obj.strModelName;
        return map;
      }, {});
      setModelCodes(modelCodesMap)
    }, {});
    setLoading(false);
    return () => { uV && uV(); uS && uS(); uC && uC(); };
  }, [onSnapshotData]);

  // quick lookup maps
  const soByNumber = useMemo(() => {
    const map = new Map();
    (salesOrders || []).forEach(so => map.set(so.strSalesOrdNbr, so));
    return map;
  }, [salesOrders]);

  const custByNbr = useMemo(() => {
    const map = new Map();
    (customers || []).forEach(c => map.set(c.strCustNbr, c));
    return map;
  }, [customers]);

  // compute grouped counts
  const { rows, totals, detailMap } = useMemo(() => {
    const countsByCustomer = new Map();
    const vehiclesByCustomer = new Map();

    (vehicles || []).forEach(v => {
      // exclude Stock SO
      if (!v.strSalesOrdNbr || v.strSalesOrdNbr === 'Stock') return;

      // Active Sales Order only
      const so = soByNumber.get(v.strSalesOrdNbr);
      if (!so || so.strSalesOrdStatus !== 'Active') return;

      // Age from dtRecvDate only (skip if missing)
      const recv = toDate(v.dtRecvDate);
      if (!recv) return;

      const age = ageInDays(recv);
      const b = bucketKey(age);
      if (!b) return;

      const customer = custByNbr.get(so.strCustNbr);
      const custName = customer?.strCustName || '(Unknown Customer)';

      if (!countsByCustomer.has(custName)) {
        countsByCustomer.set(custName, blankCounts());
        vehiclesByCustomer.set(custName, new Map());
      }
      const entry = countsByCustomer.get(custName);
      entry[b] += 1;
      entry.total += 1;

      const custBuckets = vehiclesByCustomer.get(custName);
      if (!custBuckets.has(b)) custBuckets.set(b, []);
      custBuckets.get(b).push({
        id: v.id || `${v.strSalesOrdNbr}-${v.strStockNbr || v.strVin || Math.random()}`,
        stockNumber: v.strStockNbr || (v.strVin ? v.strVin.slice(-8) : ''),
        year: v.strYR || '',
        make: v.strMake || '',
        model: resolveModelName(v, modelCodes),
      });
    });

    // sort alphabetically by customer name
    const sorted = Array.from(countsByCustomer.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([customer, c]) => ({ customer, ...c }));

    // grand totals
    const grand = blankCounts();
    sorted.forEach(r => {
      BUCKETS.forEach(({ key }) => (grand[key] += r[key]));
      grand.total += r.total;
    });

    return { rows: sorted, totals: grand, detailMap: vehiclesByCustomer };
  }, [vehicles, soByNumber, custByNbr]);

  const handleCellClick = (customer, bucketKey) => {
    const buckets = detailMap.get(customer);
    const vehiclesForBucket = buckets?.get(bucketKey) || [];
    if (!vehiclesForBucket.length) return;
    setDetail({
      customer,
      bucketKey,
      vehicles: vehiclesForBucket,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setDetail(null);
  };

  const getBucketDisplay = (key) => BUCKETS.find(b => b.key === key)?.display || key;

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 240 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <Card>
      <Card.Body>
        <div className="text-center mb-3">
          <div className="fw-bold">Hardy Fleet Group</div>
          <div>Inventory Age Report</div>
          <div className="fw-bold">Customer Summary</div>
          <div className="fw-bold">{todayStr()}</div>
        </div>

        <Table bordered responsive>
          <thead>
            <tr>
              <th style={{ width: '40%' }}>Customer</th>
              {BUCKETS.map(({ label }) => <th key={label} className="text-center">{label}</th>)}
              <th className="text-center">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.customer}>
                <td>{r.customer}</td>
                {BUCKETS.map(({ key }) => (
                  <td key={key} className="text-center">
                    {r[key] > 0 ? (
                      <button
                        type="button"
                        className="btn btn-link p-0 text-decoration-none"
                        onClick={() => handleCellClick(r.customer, key)}
                      >
                        {r[key]}
                      </button>
                    ) : (
                      dash(r[key])
                    )}
                  </td>
                ))}
                <td className="text-center fw-semibold">{dash(r.total)}</td>
              </tr>
            ))}

            {/* totals row */}
            <tr>
              <td className="fw-bold">Total</td>
              {BUCKETS.map(({ key }) => (
                <td key={key} className="text-center fw-bold">{totals[key]}</td>
              ))}
              <td className="text-center fw-bold">{totals.total}</td>
            </tr>
          </tbody>
        </Table>
      </Card.Body>

      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {detail ? `${detail.customer} — ${getBucketDisplay(detail.bucketKey)}` : 'Vehicles'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detail?.vehicles?.length ? (
            <Table bordered responsive hover className="mb-0">
              <thead>
                <tr>
                  <th style={{ minWidth: 140 }}>Stock #</th>
                  <th style={{ minWidth: 80 }}>Year</th>
                  <th style={{ minWidth: 140 }}>Make</th>
                  <th style={{ minWidth: 160 }}>Model</th>
                </tr>
              </thead>
              <tbody>
                {detail.vehicles.map(vehicle => (
                  <tr key={vehicle.id}>
                    <td>
                      {vehicle.stockNumber ? (
                        <a href={`/vehicles?search=${encodeURIComponent(vehicle.stockNumber)}&status=all`}>
                          {vehicle.stockNumber}
                        </a>
                      ) : (
                        '–'
                      )}
                    </td>
                    <td>{vehicle.year || '–'}</td>
                    <td>{vehicle.make || '–'}</td>
                    <td>{vehicle.model || '–'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center text-muted">No vehicles found for this bucket.</div>
          )}
        </Modal.Body>
      </Modal>
    </Card>
  );
}
